import { useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { AudioNote, CalendarEvent } from '@highlight/common'
import {
  useActiveAudioNoteTitle,
  useAudioNoteActiveNoteId,
  useAudioNoteAudioSessionId,
  useAudioNoteChatConversationId,
  useAudioNoteMeeting,
  useAudioNoteMeetingCalendarEventId,
  useAudioNoteSavedNote,
  useAudioNoteStartTimeMs,
  useAudioNoteStatus,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { useAudioNoteById, useAudioNotesForCalendarEvent } from '@renderer/hooks/audio-notes'
import { useCalendarEvents } from '@renderer/hooks/use-calendar-events'
import { useCurrentMeeting } from '@renderer/hooks/useCurrentMeeting'
import { getDetectedMeetingAppName, isNoteForCalendarEvent } from '@shared/utils/audio-notes/AudioNoteUtils'
import { findCalendarEventForNote } from '@shared/utils/calendar-matching'
import { getMeetingAppName } from '@shared/utils/connections/calendars'
import { useUpdateTopViewContext } from '../../view-atoms'
import { readFeedback, type FeedbackRating } from '@renderer/chat/api/feedback'
import { useAuth } from '@renderer/chat/providers'
import { useMeetingBrief } from '../../components/meeting/brief/use-meeting-brief'
import type { MeetingBrief } from '../../components/meeting/brief/types'
import { useRecordMeetingIds, useResolvedMeetingIds } from './meeting-resolution'
import { MEETING_VIEW_ID } from '../../components/meeting/meeting-view-id'
import type { MeetingTab } from './meeting-tabs'

/**
 * Latch the ids that say *which meeting this entry is* onto the entry itself.
 * A `live` entry opened the moment recording starts has no ids at all — a
 * Custom Meeting has no event, and no note until the session's active id
 * exists — and an entry that never learns them stays "the live meeting"
 * forever, so opening the same meeting by note id (a Home row, or the
 * save-time remote open) would stack a second Meeting View instead of landing
 * on this one. `viewIdentity` reads the entry's context, so the entry is where
 * these have to end up.
 *
 * Only these two: they're what identity is made of, and either re-derives from
 * the other (`meeting-resolution` joins them, and the model resolves a note
 * from its event and back). The audio session is deliberately *not* here — it
 * can't be re-derived once main nulls it at save, which is why it lives in the
 * index instead, where it also survives this entry.
 *
 * Called by the view itself, not the model — the floating-bar slot resolves the
 * same model, and two copies of this effect would both write.
 */
export function useMeetingContextLatch(
  context: MeetingViewContext,
  model: Pick<MeetingViewModel, 'variant' | 'audioNote' | 'briefCalendarEventId'>,
) {
  const updateViewContext = useUpdateTopViewContext(MEETING_VIEW_ID)
  const activeNoteId = useAudioNoteActiveNoteId()
  // While live there's no resolved note yet, but the singleton's active id
  // already names the note this session will save under. Latch it now: the
  // save-time remote open (`sidepanel:openView` by note id) is handled before
  // any post-save effect runs, so an entry that waited for the saved note to
  // latch would still read as a different document and stack a second Meeting
  // View for the meeting that just ended.
  const audioNoteId = model.audioNote?.id ?? (model.variant === 'live' ? (activeNoteId ?? undefined) : undefined)
  const calendarEventId = model.briefCalendarEventId

  useEffect(() => {
    const needsNoteId = !!audioNoteId && !context?.audioNoteId
    const needsEventId = !!calendarEventId && !context?.calendarEventId
    if (!needsNoteId && !needsEventId) return
    updateViewContext((current) => ({
      // Falls back to the context this render used, so `mode` — the one
      // required field — survives even if the entry were somehow context-less.
      ...(current ?? context),
      ...(needsNoteId ? { audioNoteId } : {}),
      ...(needsEventId ? { calendarEventId } : {}),
    }))
  }, [audioNoteId, calendarEventId, context, updateViewContext])
}

/**
 * `useAudioNoteById` has a 15-min staleTime — invalidate once the view resolves
 * which note it's showing, so a backend title write (a calendar-event title
 * backfill, say) isn't shadowed by a cached AI title from an earlier fetch.
 *
 * Called by the view, not the model, for the same reason as the latch above.
 */
export function useResolvedMeetingNoteRefresh(audioNoteId: string | null) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!audioNoteId) return
    void queryClient.invalidateQueries({ queryKey: ['audio-note', audioNoteId] })
  }, [audioNoteId, queryClient])
}

/** How this Meeting View was opened, and onto what. */
export interface MeetingViewContext {
  /**
   * `live` follows whatever recording is running — the entry Mini, the audio
   * pill, and "New Meeting" open, before any ids exist to name. `note` names
   * one specific meeting, and stays on it even while another is being recorded.
   *
   * This is the whole difference the two meeting view ids used to encode. It
   * belongs here: the destination is the same surface either way, and an entry
   * opened `live` goes on to latch the ids it resolves into this same context.
   */
  mode: 'live' | 'note'
  audioNoteId?: string
  /**
   * The note the live singleton was still carrying when this view opened for a
   * recording that hadn't started yet — see `useNewMeetingContext`. Until the
   * new session replaces it, this view has no saved note of its own.
   */
  staleSavedNoteId?: string
  /** Optional override — used when opening the view for an upcoming meeting that has no recording yet. */
  calendarEventId?: string
  title?: string
  /** The tab to land on. Omitted, the lifecycle picks (see `resolveMeetingTab`). */
  initialTab?: MeetingTab
  /** Calendar event metadata for header rendering when there's no audio note yet. */
  meeting?: CalendarEvent
  /**
   * The conversation this meeting is being chatted with, and whether it's on
   * screen. Chatting about a meeting keeps the meeting on screen: the thread
   * is the meeting's Chat tab where the layout has one, and otherwise slides
   * up over this entry as a bottom sheet instead of stacking a Chat View, the
   * same way an action's edit thread does (HIG-5977). The id outlives a
   * dismissal so the composer can offer to resume it.
   */
  chatId?: string
  chat?: boolean
  /** Set for the submit that opened the drawer, so its thread scrolls like a fresh send. */
  isFreshSubmit?: boolean
  /**
   * The id the meeting's first chat opens under, set once the meeting is known
   * to have no thread yet (see `useMeetingChatThread`). Unset, a chat opens
   * under an id the backend picks and the note keeps the one it had.
   */
  newChatId?: string
}

export interface MeetingViewModel {
  /** live = a recording for this meeting is in progress; past = a saved note / ended meeting. */
  variant: 'live' | 'past'
  /** Custom Meeting: a manual recording with no calendar event behind it. */
  isCustom: boolean
  title: string
  calendarEvent: CalendarEvent | undefined
  /** Resolved saved note (null while loading or for a not-yet-recorded meeting). */
  audioNote: AudioNote | null
  /** The note id this view resolved to — set before `audioNote` finishes loading. */
  audioNoteId: string | null
  /**
   * Live audio session behind this meeting, while one is running. Follow-ups
   * generated mid-meeting reference the session rather than the note (HIG-5841).
   */
  audioSessionId: string | null
  /**
   * The meeting's chat thread as its note (or, while live, its recording) names
   * it. The conversation may never have been created, or may have been deleted.
   */
  chatConversationId: string | undefined
  isLoading: boolean
  startedAt: Date | null
  endedAt: Date | null
  /**
   * A scheduled event with nothing recorded: opened before its start time, not
   * live, and no resolved note. "Upcoming" is latched at open so the start time
   * passing doesn't reshape the view mid-read; the recording/note parts stay
   * reactive so content appears the moment it exists.
   */
  isUpcomingWithoutAudioNote: boolean
  /** Brief keyed on the calendar event — powers the header snippet. */
  brief: MeetingBrief | null
  /** Stored rating for the brief's authoring turn; undefined while it loads. */
  briefFeedbackRating: FeedbackRating | null | undefined
  /**
   * A brief is being generated for this meeting. Optimistic and renderer-local;
   * it self-clears a few minutes in, so a generation that fails reads as "no
   * brief" again rather than as a stuck spinner.
   */
  briefGenerating: boolean
  /**
   * The brief fetch hasn't resolved yet. A brief may still land without any
   * user action, so nothing should animate off "no brief" while this is true.
   */
  briefLoading: boolean
  /** The event a brief can be generated for — briefs are keyed on the event, not the note. */
  briefCalendarEventId: string | undefined
  /** Meeting app name used to resolve electron vs recall capture permissions. */
  meetingAppName: string | undefined
}

/**
 * Resolves the single Meeting View's data across the whole lifecycle
 * (ADR 0001 / HIG-5823): live recording state, a saved note by id, or a
 * calendar event with no recording yet — all through one model so the view
 * renders presentational branches instead of separate surfaces.
 */
export function useMeetingViewModel(context: MeetingViewContext): MeetingViewModel {
  const { getAuthToken } = useAuth()
  // Per-field selectors, not useAudioNoteState(): the main process rebroadcasts
  // the whole state object on every transcript and summary tick, and this model
  // feeds the entire view.
  const status = useAudioNoteStatus()
  const liveSavedNote = useAudioNoteSavedNote()
  // A "New Meeting" open lands a full IPC round trip (and up to a 2s auth
  // hydration wait) before main tears the finished meeting down, so the
  // singleton is still holding the *previous* note when this view mounts. The
  // opener names it; ignoring it keeps the view context-less until the new
  // session arrives, instead of resolving — and then latching — the meeting the
  // user just left. Goes inert on its own once `savedNote` isn't that note.
  const savedNote = context?.staleSavedNoteId && liveSavedNote?.id === context.staleSavedNoteId ? null : liveSavedNote
  const activeNoteId = useAudioNoteActiveNoteId()
  const activeNoteTitle = useActiveAudioNoteTitle()
  const activeMeeting = useAudioNoteMeeting()
  const activeStartTime = useAudioNoteStartTimeMs()
  const liveAudioSessionId = useAudioNoteAudioSessionId()
  const liveChatConversationId = useAudioNoteChatConversationId()
  const isAudioNoteActive = status !== 'idle' || !!savedNote
  const { currentDetectedMeeting, currentScheduledMeeting } = useCurrentMeeting()

  const activeCalendarEventId = useAudioNoteMeetingCalendarEventId() ?? undefined

  // Live when a recording is in progress and this view targets it: a `live`
  // open always follows the running session; a `note` open is live only while
  // it points at the meeting being recorded.
  const matchesActive =
    (!!context?.audioNoteId && context.audioNoteId === activeNoteId) ||
    (!!context?.calendarEventId && context.calendarEventId === activeCalendarEventId)
  const followsLiveSession = context.mode === 'live' || matchesActive
  const isLive = isAudioNoteActive && !savedNote && followsLiveSession

  // When opened from an upcoming meeting there's no audioNoteId yet — only a
  // calendar event. Resolve via a metadata query keyed on that event (refreshes
  // on `audio-note:saved` with the rest of the audio-notes family).
  const { data: linkedNotesForEvent } = useAudioNotesForCalendarEvent(
    isLive || context?.audioNoteId ? undefined : context?.calendarEventId,
  )
  const resolvedAudioNoteId = useMemo(() => {
    if (isLive) return undefined
    if (context?.audioNoteId) return context.audioNoteId
    if (savedNote && followsLiveSession) return savedNote.id
    if (!context?.calendarEventId || !linkedNotesForEvent) return undefined
    const event = context?.meeting
    const match = linkedNotesForEvent.find((note) =>
      event ? isNoteForCalendarEvent(note, event) : note.meeting?.calendarEventId === context.calendarEventId,
    )
    return match?.id
  }, [
    isLive,
    context?.audioNoteId,
    context?.calendarEventId,
    context?.meeting,
    savedNote,
    followsLiveSession,
    linkedNotesForEvent,
  ])

  const ownsLiveSession = isLive || (!!savedNote && savedNote.id === resolvedAudioNoteId)
  const latchedSessionId = ownsLiveSession && liveAudioSessionId ? liveAudioSessionId : undefined

  const listNote = useMemo(
    () => (resolvedAudioNoteId ? (linkedNotesForEvent?.find((n) => n.id === resolvedAudioNoteId) ?? null) : null),
    [resolvedAudioNoteId, linkedNotesForEvent],
  )
  const { data: audioNoteById, isLoading: isNoteLoading } = useAudioNoteById(resolvedAudioNoteId)
  // While the by-id query is in-flight, fall back to the freshest local copy
  // (the just-saved note from audio state, then the list entry) so the view
  // doesn't flash empty on a note that was just saved.
  const audioNote =
    audioNoteById ??
    (resolvedAudioNoteId && savedNote?.id === resolvedAudioNoteId ? savedNote : null) ??
    (isNoteLoading ? listNote : null)

  const { data: allCalendarEvents } = useCalendarEvents()

  // Older audio notes (and detected-meeting recordings the system couldn't
  // resolve to an event at capture time) lack `meeting.calendarEventId`.
  const fallbackCalendarEventId = useMemo(
    () => findCalendarEventForNote(audioNote, allCalendarEvents)?.id,
    [audioNote, allCalendarEvents],
  )

  const calendarEventId =
    context?.calendarEventId ??
    audioNote?.meeting?.calendarEventId ??
    (isLive ? activeCalendarEventId : undefined) ??
    fallbackCalendarEventId ??
    undefined

  // The session is the one id that can't be re-derived: main nulls it the
  // moment the note saves, and the Ended → Finalized window — where the notepad
  // is still open and session-referenced follow-ups land — is right after. The
  // index is the only copy that outlives the save, so publish it there while
  // this view owns it and read it back from there once it's gone.
  const resolved = useResolvedMeetingIds({ audioNoteId: resolvedAudioNoteId, calendarEventId })
  const audioSessionId = latchedSessionId ?? resolved.audioSessionId ?? null
  useRecordMeetingIds({
    // While live the resolved id is deliberately unset, but the active id is
    // what the save-time open will name the note by — file the join under it
    // now, so `normalize` can resolve that open to this meeting's calendar
    // event at the moment the event lands (before any post-save effect runs).
    audioNoteId: resolvedAudioNoteId ?? (isLive ? (activeNoteId ?? undefined) : undefined),
    audioSessionId: audioSessionId ?? undefined,
    calendarEventId,
  })

  const calendarEvent = useMemo<CalendarEvent | undefined>(() => {
    if (calendarEventId && allCalendarEvents) {
      const live = allCalendarEvents.find((e) => e.id === calendarEventId)
      if (live) return live
    }
    return context?.meeting
  }, [calendarEventId, allCalendarEvents, context?.meeting])

  const title = useMemo(() => {
    if (isLive) {
      if (currentScheduledMeeting && 'title' in currentScheduledMeeting && currentScheduledMeeting.title) {
        return currentScheduledMeeting.title
      }
      if (calendarEvent?.title) return calendarEvent.title
      if (currentDetectedMeeting && 'windowTitle' in currentDetectedMeeting && currentDetectedMeeting.windowTitle) {
        return currentDetectedMeeting.windowTitle
      }
      if (activeNoteTitle) return activeNoteTitle
      if (activeMeeting && 'app' in activeMeeting && activeMeeting.app?.name) return activeMeeting.app.name
      return 'Meeting'
    }
    return audioNote?.title || context?.title || calendarEvent?.title || 'Meeting'
  }, [
    isLive,
    currentScheduledMeeting,
    currentDetectedMeeting,
    calendarEvent,
    activeNoteTitle,
    activeMeeting,
    audioNote?.title,
    context?.title,
  ])

  const startedAt = calendarEvent?.start
    ? new Date(calendarEvent.start)
    : audioNote?.startedAt
      ? new Date(audioNote.startedAt)
      : isLive && activeStartTime
        ? new Date(activeStartTime)
        : null
  const endedAt = calendarEvent?.end
    ? new Date(calendarEvent.end)
    : audioNote?.endedAt && !isLive
      ? new Date(audioNote.endedAt)
      : null
  // Latched once per view instance: a `Date.now()` comparison re-evaluated per
  // render would flip the flag mid-view the moment the start time passes —
  // collapsing an open brief and surfacing empty recorded sections. The parts
  // that mean content actually exists (a live recording, a resolved note id)
  // stay reactive, so the sections appear the moment there's something to show.
  const wasUpcomingAtOpen = useRef<boolean | null>(null)
  if (wasUpcomingAtOpen.current === null && calendarEvent) {
    wasUpcomingAtOpen.current = new Date(calendarEvent.start).getTime() > Date.now()
  }
  // `!resolvedAudioNoteId` rather than `!audioNote`: the note id resolves before
  // the note body loads (no hidden-sections flash), and `!isLive` because a live
  // recording never has a note yet — joining a meeting early must not read as
  // "nothing recorded" and hide the live transcript.
  const isUpcomingWithoutAudioNote = !!wasUpcomingAtOpen.current && !isLive && !resolvedAudioNoteId

  // Brief bodies are stripped from calendar GlobalState — fetch on open.
  // Poll a cached 404 while generation is in flight or presence has landed,
  // otherwise the Infinity staleTime leaves the body invisible until reload.
  const {
    data: fetchedBrief,
    isLoading: briefQueryLoading,
    isGenerating: briefQueryGenerating,
    isFailed: briefQueryFailed,
    isRegenerating,
  } = useMeetingBrief(calendarEventId, {
    pollWhilePending: !!calendarEvent?.briefGenerating || !!calendarEvent?.brief?.conversationId,
    presenceConversationId: calendarEvent?.brief?.conversationId,
  })
  const inlineBriefText = calendarEvent?.brief?.brief?.trim()
  const brief =
    fetchedBrief ??
    (inlineBriefText && calendarEvent?.brief
      ? {
          brief: calendarEvent.brief.brief,
          relevantLinks: calendarEvent.brief.relevantLinks,
          conversationId: calendarEvent.brief.conversationId,
        }
      : null)
  const feedbackConversationId = brief?.conversationId ?? calendarEvent?.brief?.conversationId
  const { data: briefFeedbackRating } = useQuery({
    queryKey: feedbackConversationId
      ? ['feedback', 'conversation', feedbackConversationId]
      : ['feedback', 'conversation'],
    queryFn: async ({ signal }) => {
      if (!feedbackConversationId) return null
      const token = await getAuthToken()
      if (!token) throw new Error('Missing auth token for meeting brief feedback')
      return readFeedback({ conversationId: feedbackConversationId }, token, signal)
    },
    enabled: !!feedbackConversationId,
  })

  return {
    variant: isLive ? 'live' : 'past',
    isCustom: !calendarEvent,
    title,
    calendarEvent,
    audioNote,
    audioNoteId: resolvedAudioNoteId ?? null,
    // Never the raw live id when this view doesn't own it — a back-to-back
    // meeting must not lend its session to the previous one. The index can't:
    // it only ever answers for the ids this meeting is filed under.
    audioSessionId,
    chatConversationId: isLive ? (liveChatConversationId ?? undefined) : audioNote?.chatConversationId,
    isLoading: !isLive && !audioNote && isNoteLoading && !!context?.audioNoteId,
    startedAt,
    endedAt,
    isUpcomingWithoutAudioNote,
    brief,
    briefFeedbackRating,
    // The server's `failed` verdict beats the optimistic calendar flag —
    // otherwise a failed run reads as "Preparing…" until the flag times out.
    briefGenerating: (!!calendarEvent?.briefGenerating && !briefQueryFailed) || briefQueryGenerating || isRegenerating,
    briefLoading: briefQueryLoading && !brief,
    briefCalendarEventId: calendarEventId,
    meetingAppName:
      (isLive ? getDetectedMeetingAppName(activeMeeting) : undefined) ??
      (calendarEvent ? getMeetingAppName(calendarEvent) : undefined),
  }
}
